import { useEffect, useRef, useState } from 'react';

/**
 * True when the point is currently over a surface tagged `data-surface="dark"`
 * (the reel player, contact card, menu overlay…). This is what lets fixed
 * chrome — the logo, the nav pills — flip between ink and white based on what
 * is actually painted beneath them, not on a guessed scroll threshold.
 *
 * Occlusion-aware: elementsFromPoint is ordered top→bottom, and the FIRST
 * tagged surface wins. The preloader sheet is tagged light, so while it still
 * covers the dark reel the chrome stays ink — and flips to white at the exact
 * moment the sheet's bottom-up wipe edge passes the sampled point.
 */
export const useSurfaceIsDark = (getPoint: () => { x: number; y: number } | null, initial = true) => {
  const [dark, setDark] = useState(initial);
  const getPointRef = useRef(getPoint);
  getPointRef.current = getPoint;

  useEffect(() => {
    let raf = 0;
    let frame = 0;
    const tick = () => {
      // every 3rd frame (~20x/s) so the flip tracks the wipe edge closely
      if (frame++ % 3 === 0) {
        const pt = getPointRef.current();
        if (pt && pt.x > 0 && pt.y > 0 && pt.x < window.innerWidth && pt.y < window.innerHeight) {
          const stack = document.elementsFromPoint(pt.x, pt.y) as HTMLElement[];
          let next = false;
          for (const n of stack) {
            const s = n?.dataset?.surface;
            if (s === 'dark') {
              next = true;
              break;
            }
            if (s === 'light') break;
          }
          setDark(next);
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  return dark;
};
